import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  Tooltip
} from '@mui/material';
import { Search as SearchIcon, TrendingUp, TrendingDown } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { styled } from '@mui/material/styles';
import api from '../../../services/api';
import logger from '../../../utils/logger';
import { formatDateTime } from '../../../utils/dateUtils';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  },
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  color: '#ffffff',
  fontWeight: 600,
  padding: '16px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#eff6ff',
  },
}));

const ScoreChip = styled(Chip)(({ score }) => {
  let color = 'default';
  if (score >= 250) color = 'success';
  else if (score >= 200) color = 'info';
  else if (score >= 150) color = 'warning';
  else color = 'error';

  return {
    fontWeight: 600,
    fontSize: '14px',
    minWidth: '80px'
  };
});

const ClientScores = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredScores, setFilteredScores] = useState([]);

  useEffect(() => {
    fetchClientScores();
  }, [page, rowsPerPage]);

  useEffect(() => {
    // Filter scores based on search term
    if (searchTerm.trim() === '') {
      setFilteredScores(scores);
    } else {
      const filtered = scores.filter(score =>
        score.userdetails?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        score.userdetails?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        score.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredScores(filtered);
    }
  }, [searchTerm, scores]);

  const fetchClientScores = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/customer/gmb-profile-socre/admin/get-all-client-scores?page=${page + 1}&pageSize=${rowsPerPage}`);

      logger.log('[ClientScores] Response:', response);

      if (response?.data?.data) {
        setScores(response.data.data);
        setTotalItems(response.data.pagination?.totalItems || 0);
      }
    } catch (error) {
      logger.error('[ClientScores] Error fetching scores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getScoreColor = (score) => {
    if (score >= 250) return 'success';
    if (score >= 200) return 'info';
    if (score >= 150) return 'warning';
    return 'error';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  if (loading && scores.length === 0) {
    return (
      <Container>
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress sx={{ color: '#3b82f6' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading scores...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleSection>
          <TitleIcon>
            <AssessmentIcon fontSize="large" />
          </TitleIcon>
          <Box>
            <Typography variant="h4" fontWeight={700} color="#1f2937">Client GMB Profile Scores</Typography>
            <Typography variant="body2" color="text.secondary">View and track all client profile optimization scores</Typography>
          </Box>
        </TitleSection>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchField
            placeholder="Search by client name, email, or location..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#3b82f6' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 400 }}
          />
        </Box>
      </Header>

      <TableContainerStyled component={Paper}>
        {filteredScores.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <AssessmentIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography color="text.secondary">
              {searchTerm ? 'No matching scores found' : 'No client scores available'}
            </Typography>
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <Th>Client Name</Th>
                <Th>Email</Th>
                <Th>Location</Th>
                <Th align="center">Score</Th>
                <Th>Date Checked</Th>
                <Th align="center">Status</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredScores.map((scoreData, index) => {
                const score = scoreData.total_score || 0;
                const user = scoreData.userdetails || {};

                return (
                  <StyledTableRow key={scoreData.id || index}>
                    <Td>
                      <Typography variant="subtitle2" fontWeight={600} color="#1f2937">
                        {user.full_name || 'N/A'}
                      </Typography>
                    </Td>
                    <Td>
                      <Typography variant="body2" color="#4b5563">
                        {user.email || 'N/A'}
                      </Typography>
                    </Td>
                    <Td>
                      <Tooltip title={scoreData.address || scoreData.location || 'No address'}>
                        <Typography
                          variant="body2"
                          color="#4b5563"
                          sx={{
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {scoreData.location || 'N/A'}
                        </Typography>
                      </Tooltip>
                    </Td>
                    <Td align="center">
                      <ScoreChip
                        label={`${score} / 300`}
                        color={getScoreColor(score)}
                        score={score}
                        size="small"
                      />
                    </Td>
                    <Td>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(scoreData.created_at)}
                      </Typography>
                    </Td>
                    <Td align="center">
                      {score >= 250 ? (
                        <Chip
                          icon={<TrendingUp />}
                          label="Excellent"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : score >= 200 ? (
                        <Chip
                          label="Good"
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      ) : score >= 150 ? (
                        <Chip
                          label="Needs Work"
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<TrendingDown />}
                          label="Poor"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Td>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainerStyled>

      <Box sx={{ mt: 2, bgcolor: '#fff', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>
    </Container>
  );
};

export default ClientScores;

